import autobind from 'autobind-decorator';
import type { MessageInstance } from 'ant-design-vue/es/message/interface';
import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import { getBusiOnlineStatus, getUnreadMsgCounts } from '~/api/im';
import type { BusiOnlineStatusResponseVO, ImUserVO, MsgVO } from '~/types/im';
import { IM_BUSINESS } from './constant';
import { MessageService } from './message.service';
import { SdkService } from './sdk.service';
import {
  getStudentId,
  getTeacherId,
  isAudioMsg,
  isImgMsg,
  isPhoneMsg,
  isVideoMsg,
} from './utils';

@Injectable()
export class ImService {
  // 显示入口还是聊天框
  public entryVisible = true;
  // 自己是学生还是老师
  public selfType = 'ms';
  // 自己是否在线
  public selfOnline = true;
  // 自己的用户信息
  public self = {} as ImUserVO;
  // 带前缀的用户ID
  public selfId = '';
  // 对方是否在线
  public peerOnline = true;
  // 对方的用户信息
  public peer = {} as ImUserVO;
  // 带前缀的对方用户ID
  public peerId = '';
  // 未读消息数量
  public unreadCount = 0;
  // 消息列表
  public messages = [] as MsgVO[];
  // 输入框文本
  public input = '';
  // 聊天框内部的message提示API
  public messageApi!: MessageInstance;

  @Inject(forwardRef(() => SdkService))
  public sdkService!: SdkService;

  @Inject(forwardRef(() => MessageService))
  public messageService!: MessageService;

  public init(type: string, self: ImUserVO, peer: ImUserVO) {
    this.selfType = type;
    this.self = self;
    this.selfId = getStudentId(self.id);
    this.peer = peer;
    this.peerId = getTeacherId(peer.id);

    // 初始化老师在线状态
    this.updateTeacherStatusFromRemote();
    // 初始化未读消息数量
    this.updateUnreadCountFromRemote();
    // 建立IM消息链接
    this.sdkService.init();
    // 加载第一页的历史消息
    this.messageService.init();
  }

  @autobind
  public toggleEntry() {
    this.entryVisible = !this.entryVisible;
  }

  public setSelfOnline(online: boolean) {
    this.selfOnline = online;
  }

  public increaseUnread(msg?: MsgVO) {
    // 需要排除掉Phone消息，Phone消息虽然也是渲染消息，但是不记录未读消息数量
    if (msg) {
      if (!isPhoneMsg(msg)) {
        this.unreadCount += 1;
      }
    }
  }

  public increaseUnreadIfNeedPlay(msg?: MsgVO) {
    // 需要用户手动播放的消息，需要特别记录未读消息数量
    if (msg) {
      if (isAudioMsg(msg) || isVideoMsg(msg)) {
        this.unreadCount += 1;
      } else {
        // 设置为已经是已读状态
        // 后续真正触发已读时，unreadCount不需要再减去1
        msg.preRead = true;
      }
    }
  }

  public decreaseUnread() {
    if (this.unreadCount > 0) {
      this.unreadCount -= 1;
    }
  }

  public updateTeacherStatusFromRemote() {
    const model = {
      business: IM_BUSINESS,
      user: this.selfId,
      users: [this.peerId],
    };
    return getBusiOnlineStatus(model).then((res) => {
      this.updateTeacherStatus(res);
    });
  }

  public updateTeacherStatus(res: BusiOnlineStatusResponseVO) {
    if (res?.statusInfo) {
      const teacher = res.statusInfo[0];
      if (teacher && teacher.imStatus && teacher.user === this.peerId) {
        this.peerOnline = teacher.imStatus.status === 1;
      }
    }
  }

  public updateUnreadCountFromRemote() {
    const model = {
      user: this.selfId,
    };
    const teacherId = this.peerId;
    return getUnreadMsgCounts(model).then((res) => {
      if (res?.userCounts) {
        this.unreadCount = res.userCounts[teacherId] || 0;
      }
    });
  }
}
